import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API, BACKEND_URL } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ImageIcon, Plus, Trash2, X, Upload, User } from 'lucide-react';
import LazyImage from '../components/LazyImage';

import AppLogo from '../components/AppLogo';

const PhotoGalleryPage = () => {
  const { user } = useContext(AuthContext);
  const [circles, setCircles] = useState([]);
  const [selectedCircleId, setSelectedCircleId] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [caption, setCaption] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const fetchPhotos = useCallback(async (circleId) => {
    try {
      const res = await axios.get(`${API}/circles/${circleId}/photos`);
      setPhotos(res.data);
    } catch (e) { toast.error('Could not load photos'); }
  }, []);

  const fetchCircles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/circles`);
      setCircles(res.data);
      if (res.data.length > 0) setSelectedCircleId(res.data[0].id);
    } catch (e) { toast.error('Could not load circles'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCircles(); }, [fetchCircles]);

  useEffect(() => {
    if (selectedCircleId) fetchPhotos(selectedCircleId);
  }, [selectedCircleId, fetchPhotos]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10MB)'); return; }
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCircleId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('caption', caption);
      await axios.post(`${API}/circles/${selectedCircleId}/photos?caption=${encodeURIComponent(caption)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo added to gallery!');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setPreview(null);
      setCaption('');
      fetchPhotos(selectedCircleId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/photos/${photoId}`);
      toast.success('Photo removed');
      setViewPhoto(null);
      fetchPhotos(selectedCircleId);
    } catch (error) {
      toast.error('Could not delete');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600"></div></div>;
  }

  return (
    <div className="min-h-screen pb-24" data-testid="photo-gallery-page">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppLogo />
            <div>
              <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Photo Gallery</h1>
              <p className="text-xs text-muted-foreground">Moments shared by your circle</p>
            </div>
          </div>
          <Button className="btn-primary h-9 text-sm" onClick={() => setUploadDialogOpen(true)} data-testid="add-photo-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Photo
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-4 space-y-5">
        {/* Circle selector */}
        {circles.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {circles.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedCircleId(c.id)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  selectedCircleId === c.id ? 'bg-cyan-600 text-white' : 'bg-card text-muted-foreground border border-border/50 hover:bg-muted'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Photo Grid */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {photos.map(photo => (
              <button
                key={photo.id}
                onClick={() => setViewPhoto(photo)}
                className="relative aspect-square rounded-xl overflow-hidden group shadow-sm hover:shadow-md transition-all"
                data-testid={`photo-${photo.id}`}
              >
                <LazyImage
                  src={`${BACKEND_URL}/api/files/${photo.image_path}`}
                  alt={photo.caption || 'Gallery photo'}
                  className="w-full h-full group-hover:scale-105 transition-transform duration-300"
                />
                {photo.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                    <p className="text-[10px] text-white truncate">{photo.caption}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 surface-card">
            <ImageIcon className="w-14 h-14 text-cyan-200 mx-auto mb-4" />
            <p className="text-base font-semibold text-foreground mb-1">No photos yet</p>
            <p className="text-xs text-muted-foreground/60 mb-5 max-w-xs mx-auto">Share moments with your circle. Upload photos of visits, meals, milestones, or just a smile.</p>
            <Button onClick={() => setUploadDialogOpen(true)} className="btn-primary" data-testid="empty-add-photo-btn">
              <ImageIcon className="w-4 h-4 mr-1.5" /> Add First Photo
            </Button>
          </div>
        )}
      </main>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => { setUploadDialogOpen(open); if (!open) { setSelectedFile(null); setPreview(null); setCaption(''); } }}>
        <DialogContent className="surface-card border-0 shadow-xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add Photo</DialogTitle>
            <DialogDescription>Upload a photo to your circle gallery</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {circles.length > 1 && (
              <Select value={selectedCircleId} onValueChange={setSelectedCircleId}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select circle" /></SelectTrigger>
                <SelectContent>{circles.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {preview ? (
              <div className="relative rounded-xl overflow-hidden">
                <img src={preview} alt="Preview" className="w-full max-h-64 object-cover" />
                <button onClick={() => { setSelectedFile(null); setPreview(null); }} className="absolute top-2 right-2 w-7 h-7 bg-black/50 rounded-full flex items-center justify-center">
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-cyan-200 rounded-xl py-12 flex flex-col items-center gap-2 hover:bg-cyan-50/50 transition-colors"
                data-testid="photo-dropzone"
              >
                <Upload className="w-8 h-8 text-cyan-300" />
                <p className="text-sm text-muted-foreground">Tap to select a photo</p>
                <p className="text-[10px] text-muted-foreground/50">JPG, PNG, GIF, WebP up to 10MB</p>
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="photo-file-input" />
            <Input data-testid="photo-caption-input" placeholder="Add a caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} className="rounded-xl" />
            <Button onClick={handleUpload} disabled={!selectedFile || uploading} className="w-full btn-primary" data-testid="upload-photo-btn">
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Viewer */}
      <Dialog open={!!viewPhoto} onOpenChange={() => setViewPhoto(null)}>
        <DialogContent className="surface-card border-0 shadow-xl max-w-lg p-0 overflow-hidden">
          {viewPhoto && (
            <>
              <LazyImage src={`${BACKEND_URL}/api/files/${viewPhoto.image_path}`} alt={viewPhoto.caption || ''} className="w-full max-h-[60vh] bg-black" />
              <div className="p-4 space-y-2">
                {viewPhoto.caption && <p className="text-sm text-foreground font-medium">{viewPhoto.caption}</p>}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <User className="w-3 h-3" />
                    <span>{viewPhoto.user?.name}</span>
                    <span>&middot;</span>
                    <span>{new Date(viewPhoto.created_at).toLocaleDateString()}</span>
                  </div>
                  {viewPhoto.user_id === user?.id && (
                    <button onClick={() => handleDeletePhoto(viewPhoto.id)} className="text-xs text-rose-500 hover:text-rose-600 flex items-center gap-1" data-testid="delete-photo-btn">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoGalleryPage;
